/**
 * Sidebar de navegación principal
 * Incluye logo, menú de navegación y perfil de usuario
 * Soporta modo colapsado para maximizar espacio de trabajo
 */
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import {
    LayoutDashboard,
    UserCircle,
    Settings,
    LogOut,
    Package,
    ShoppingCart,
    Receipt,
    Truck,
    Wallet,
    Building2,
    CreditCard,
    BarChart3,
    TrendingUp,
    ChevronLeft,
    ChevronRight,
    Tags,
    Boxes,
    FileText,
} from 'lucide-react';
import { Button } from './ui/button';
import { useLowStockCount } from '@/features/products/hooks/useLowStock';
import { usePermissions } from '@/hooks/usePermissions';
import { BranchSwitcher } from '@/features/branches/components/BranchSwitcher';
import { Access, PERMISSIONS, homePathFor } from '@/lib/permissions';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';

interface SidebarProps {
    readonly user: any;
    readonly onLogout: () => void;
    readonly collapsed?: boolean;
    readonly onToggle?: () => void;
}

interface NavItem {
    title: string;
    href: string;
    icon: React.ElementType;
    badgeKey?: 'lowStock'; // Claves para badges dinámicos
    shortcut?: string; // Atajo de teclado (ej: 'F1')
    access: Access; // Obligatorio: qué rol/permiso lo ve (nada queda abierto por omisión)
}

const navItems: NavItem[] = [
    {
        title: 'Inicio',
        href: '/dashboard',
        access: 'admin',
        icon: LayoutDashboard,
        shortcut: 'F1',
    },
    {
        title: 'Ventas',
        href: '/sales',
        access: PERMISSIONS.SALES_READ_OWN,
        icon: ShoppingCart,
        shortcut: 'F2', // Navega + abre modal nueva venta
    },
    {
        title: 'Proformas',
        href: '/proformas',
        access: PERMISSIONS.PROFORMAS_MANAGE,
        icon: FileText,
    },
    {
        title: 'Caja',
        href: '/cash-register',
        access: PERMISSIONS.CASH_OPERATE,
        icon: Wallet,
        shortcut: 'F3',
    },
    {
        title: 'Cuentas Corrientes',
        href: '/customer-accounts',
        access: 'admin',
        icon: CreditCard,
        shortcut: 'F4',
    },
    {
        title: 'Ingresos',
        href: '/incomes',
        access: 'admin',
        icon: TrendingUp,
        shortcut: 'F5', // Navega + abre modal nuevo ingreso
    },
    {
        title: 'Compras',
        href: '/purchases',
        access: 'admin',
        icon: Truck,
        shortcut: 'F6', // Navega + abre modal nueva compra
    },
    {
        title: 'Gastos',
        href: '/expenses',
        access: 'admin',
        icon: Receipt,
        shortcut: 'F7', // Navega + abre modal nuevo gasto
    },
    {
        title: 'Productos',
        href: '/products',
        access: PERMISSIONS.CATALOG_MANAGE,
        icon: Package,
        badgeKey: 'lowStock',
        shortcut: 'F8',
    },
    {
        title: 'Inventario',
        href: '/inventory',
        access: PERMISSIONS.CATALOG_MANAGE, // el cajero solo agrega stock (lo fuerza el backend)
        icon: Boxes,
    },
    {
        title: 'Categorías',
        href: '/categories',
        access: 'admin',
        icon: Tags,
    },
    {
        title: 'Clientes',
        href: '/customers',
        access: 'admin',
        icon: UserCircle,
        shortcut: 'F9',
    },
    {
        title: 'Proveedores',
        href: '/suppliers',
        access: 'admin',
        icon: Building2,
        shortcut: 'F10',
    },
    {
        title: 'Reportes',
        href: '/reports',
        access: 'admin',
        icon: BarChart3,
        shortcut: 'F11',
    },
    {
        title: 'Configuración',
        href: '/settings',
        access: 'admin',
        icon: Settings,
    },
];

export function Sidebar({ user, onLogout, collapsed = false, onToggle }: SidebarProps) {
    const location = useLocation();
    const { role, isAdmin, canAccess } = usePermissions();
    // Solo el admin ve Productos, así que al resto no se le consulta el stock bajo
    const { count: lowStockCount } = useLowStockCount({ enabled: isAdmin });
    const visibleItems = navItems.filter((item) => canAccess(item.access));
    const [version, setVersion] = useState<string>('');

    useEffect(() => {
        const electronAPI = (globalThis as unknown as { electronAPI?: { getAppVersion?: () => Promise<string> } }).electronAPI;
        if (electronAPI?.getAppVersion) {
            electronAPI.getAppVersion()
                .then((v) => setVersion(v))
                .catch((err) => console.error('Error getting app version', err));
        }
    }, []);

    // Mapa de badges dinámicos
    const badges: Record<string, number> = {
        lowStock: lowStockCount,
    };

    return (
        <TooltipProvider delayDuration={0}>
            <div
                className={cn(
                    "flex flex-col h-dvh lg:h-full bg-card border-r border-border transition-all duration-300 relative",
                    collapsed ? "lg:w-16 w-72" : "w-72 lg:w-64"
                )}
            >
                {/* Botón de toggle */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggle}
                    className="hidden md:inline-flex absolute -right-3 top-7 z-50 h-6 w-6 rounded-full border border-border bg-card shadow-sm hover:bg-accent"
                >
                    {collapsed ? (
                        <ChevronRight className="h-3 w-3" />
                    ) : (
                        <ChevronLeft className="h-3 w-3" />
                    )}
                </Button>

                {/* Header / Logo */}
                <div className={cn("p-6 border-b border-border", collapsed && "px-3 py-4")}>
                    <Link to={homePathFor(role)} className="flex items-center justify-center gap-3 ">
                        {collapsed ? (
                            <div className="h-9 w-9 rounded-xl bg-black flex items-center justify-center font-black text-lg flex-shrink-0" style={{ color: '#FFC72C' }}>
                                P
                            </div>
                        ) : (
                            <span className="text-2xl font-black tracking-tight leading-none select-none" aria-label="PosBIT">
                                <span className="text-black dark:text-white">POS</span><span style={{ color: '#FFC72C' }}>BIT</span>
                            </span>
                        )}
                    </Link>
                    <BranchSwitcher collapsed={collapsed} />
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4 px-3">
                    <div className="space-y-1">
                        {visibleItems.map((item) => {
                            const isActive = location.pathname === item.href;
                            const Icon = item.icon;
                            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

                            const linkContent = (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    className={cn(
                                        'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                                        isActive
                                            ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/25'
                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                                        collapsed && 'justify-center px-2'
                                    )}
                                >
                                    <Icon
                                        className={cn(
                                            'h-5 w-5 transition-colors flex-shrink-0',
                                            isActive
                                                ? 'text-primary-foreground'
                                                : 'text-muted-foreground group-hover:text-accent-foreground'
                                        )}
                                    />
                                    {!collapsed && (
                                        <>
                                            <span className="flex-1">{item.title}</span>
                                            {item.shortcut && (
                                                <kbd className="pointer-events-none flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-bold text-foreground opacity-100 shadow-sm ml-auto">
                                                    {item.shortcut}
                                                </kbd>
                                            )}
                                            {badgeCount > 0 ? (
                                                <span
                                                    className={cn(
                                                        'flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold',
                                                        isActive
                                                            ? 'bg-primary-foreground/20 text-primary-foreground'
                                                            : 'bg-destructive text-destructive-foreground'
                                                    )}
                                                >
                                                    {badgeCount > 99 ? '99+' : badgeCount}
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                    {collapsed && badgeCount > 0 && (
                                        <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-destructive" />
                                    )}
                                </Link>
                            );

                            // En modo colapsado, envolver con tooltip
                            if (collapsed) {
                                return (
                                    <Tooltip key={item.href}>
                                        <TooltipTrigger asChild>
                                            <div className="relative">
                                                {linkContent}
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="right" className="flex items-center gap-2">
                                            {item.title}
                                            {badgeCount > 0 && (
                                                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-semibold text-destructive-foreground">
                                                    {badgeCount > 99 ? '99+' : badgeCount}
                                                </span>
                                            )}
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            }

                            return linkContent;
                        })}
                    </div>
                </nav>

                {/* User Profile & Logout */}
                <div className={cn("p-4 border-t border-border bg-muted/30", collapsed && "px-2")}>
                    {collapsed ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    onClick={onLogout}
                                >
                                    <LogOut className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                                Cerrar Sesión
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        <>
                            <div className="flex items-center gap-3 mb-3 px-1">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm flex-shrink-0">
                                    {user?.firstName?.[0]}
                                    {user?.lastName?.[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">
                                        {user?.firstName} {user?.lastName}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                        {user?.username}
                                    </p>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                onClick={onLogout}
                            >
                                <LogOut className="mr-2 h-4 w-4" />
                                Cerrar Sesión
                            </Button>
                            {version && (
                                <p className="text-[10px] text-muted-foreground/50 text-center mt-2 font-mono">
                                    v{version}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </TooltipProvider >
    );
}
